import EmptyState from 'components/EmptyState';
import { BarChartOutlined } from 'components/icons';
import { CustomTableEmptyStateProps, FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import { Skeleton } from 'components/ui/skeleton';
import { ComponentProps, useCallback } from 'react';
import { ReportPage } from 'types/reporting/reportPage';
import { cn } from 'utils/cn';

type ListProps<Row extends object, Totals> = ComponentProps<typeof ListTableWithFilters<Row, ReportPage<Row, Totals>>>;

type Props<Row extends object, Totals> = {
    data: ReportPage<Row, Totals> | undefined;
    isFetching: boolean;
    isError: boolean;
    fetchData: ListProps<Row, Totals>['fetchData'];
    filterConfig: FilterConfig;
    /** The report's starting filters, such as its default year. */
    initialSearchParams?: string;
    /** Names the rows in the list's count; without it the count is hidden, as rows of a chart are no unit. */
    countTextIds?: { singular: string; plural: string };
    /** The report opens with a row of key figures, which its loading state keeps room for. */
    hasFigures?: boolean;
    /** What the report's usage events call it, `report-<key>` as a table report's columns are kept. */
    analyticsSurface: string;
    /** The charts and tables over the rows the filters selected, with the report's totals. */
    children: (rows: Row[], totals: Totals) => React.ReactNode;
};

/**
 * A chart report's body under the list's filter bar: the filters, search, URL state and saved views work as
 * in a table report, and the rows they select are drawn by `children` instead of a table.
 */
export function ChartReportView<Row extends object, Totals>({
    data,
    isFetching,
    isError,
    fetchData,
    filterConfig,
    initialSearchParams,
    countTextIds,
    hasFigures = false,
    analyticsSurface,
    children,
}: Props<Row, Totals>) {
    const renderCharts = useCallback(
        (
            rows: Row[],
            initialLoad: boolean,
            noData: boolean,
            textId?: string,
            _pagination?: unknown,
            _loadMore?: () => void,
            emptyStateProps?: CustomTableEmptyStateProps
        ) => {
            if (initialLoad || (!data && isFetching)) return <ChartReportSkeleton withFigures={hasFigures} />;
            if (noData || !data) {
                return (
                    <EmptyState
                        size="page"
                        variant={emptyStateProps?.variant ?? 'empty'}
                        icon={BarChartOutlined}
                        titleId={textId}
                        bodyId="reporting.no-rows-body"
                        onClearFilters={emptyStateProps?.onClearFilters}
                        onRetry={emptyStateProps?.onRetry}
                    />
                );
            }
            return (
                <div
                    aria-busy={isFetching}
                    className={cn(
                        'not-new:motion-reduce:transition-none new:gap-(--card-gap) flex flex-col gap-4 pt-1 pb-6 transition-opacity duration-150',
                        isFetching && 'opacity-60'
                    )}
                >
                    {children(rows, data.totals)}
                </div>
            );
        },
        [children, data, hasFigures, isFetching]
    );
    return (
        <ListTableWithFilters<Row, ReportPage<Row, Totals>>
            data={data}
            isLoading={isFetching}
            isError={isError}
            fetchData={fetchData}
            filterConfig={filterConfig}
            initialSearchParams={initialSearchParams}
            renderCustomTable={renderCharts}
            hideElementCount={!countTextIds}
            singularTextId={countTextIds?.singular}
            pluralTextId={countTextIds?.plural}
            noEntriesId="reporting.no-rows"
            noEntriesBodyId="reporting.no-rows-body"
            analyticsSurface={analyticsSurface}
        />
    );
}

/** The shape of a chart report while its first rows load: a row of figures, a chart and a table. */
export function ChartReportSkeleton({ withFigures = false }: { withFigures?: boolean }) {
    return (
        <div className="new:gap-(--card-gap) flex flex-col gap-4 pt-1" aria-hidden>
            {withFigures && (
                <div className="new:gap-(--card-gap) grid grid-cols-[repeat(auto-fit,minmax(13rem,1fr))] gap-3">
                    <Skeleton className="h-24 rounded-xl" />
                    <Skeleton className="h-24 rounded-xl" />
                    <Skeleton className="h-24 rounded-xl" />
                </div>
            )}
            <Skeleton className="h-80 rounded-xl" />
            <Skeleton className="h-64 rounded-xl" />
        </div>
    );
}
